import React, { useEffect, useState, useCallback } from 'react';
import { Drawer, Box, Typography, List, ListItem, ListItemText, CircularProgress, IconButton, Button,Dialog, DialogTitle,DialogContent,DialogContentText,DialogActions } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useApi from 'hooks/useApi';
import { useDropzone } from 'react-dropzone';
import { ReactComponent as PdfIcon } from 'assets/images/icons/pdf.svg';
import { ReactComponent as DocIcon } from 'assets/images/icons/doc.svg';
import { ReactComponent as ImageIcon } from 'assets/images/icons/image.svg';
import { ReactComponent as FileIcon } from 'assets/images/icons/file.svg';

interface DocumentsPanelProps {
  open: boolean;
  onClose: () => void;
  entityId: string; // can be flowId, transactionId, etc.
  entityType: 'userflow' | 'authkey'; // or string, for flexibility
}

interface DocumentItem {
  _id: string;
  file_path: string;
  file_name: string;
  public_url: string;
  status: boolean;
  createdAt?: string; // Optional, depending on your API response
  updatedAt?: string; // Optional, depending on your API response
}

const DocumentsPanel: React.FC<DocumentsPanelProps> = ({ open, onClose, entityId, entityType }) => {
  const { uploadDocument, getDocuments, deleteDocument } = useApi(); // Make sure your API supports entityType
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDocId, setDeleteDocId] = useState<string | null>(null);
  const loadDocuments = async () => {
    setLoading(true);
    try {
      const docs = await getDocuments(entityId, entityType);
      setDocuments(docs || []);
    } catch (e) {
      setDocuments([]);
    }
    setLoading(false);
  };
const handleDeleteDocument = (documentId: string) => {
  setDeleteDocId(documentId);
  setDeleteDialogOpen(true);
};

const confirmDeleteDocument = async () => {
  if (deleteDocId) {
    await deleteDocument(deleteDocId);
    setDocuments((prevDocs) => prevDocs.filter((doc) => doc._id !== deleteDocId));
    setDeleteDocId(null);
    setDeleteDialogOpen(false);
  }
};

const cancelDeleteDocument = () => {
  setDeleteDocId(null);
  setDeleteDialogOpen(false);
};

  useEffect(() => {
    if (open) loadDocuments();
    // eslint-disable-next-line
  }, [open, entityId, entityType]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) return;
      setUploading(true);
      try {
        await uploadDocument(acceptedFiles[0], entityId, entityType);
        await loadDocuments();
      } catch (e) {}
      setUploading(false);
    },
    [entityId, entityType]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: uploading
  });

  function getFileIcon(fileName: string) {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (!ext) return <FileIcon style={{ marginRight: 16, width: 24, height: 24 }} />;
    if (ext === 'pdf') return <PdfIcon style={{ marginRight: 16, width: 24, height: 24 }} />;
    if (['doc', 'docx'].includes(ext)) return <DocIcon style={{ marginRight: 16, width: 24, height: 24 }} />;
    if (['png', 'jpg', 'jpeg', 'svg'].includes(ext)) return <ImageIcon style={{ marginRight: 16, width: 24, height: 24 }} />;
    return <FileIcon style={{ marginRight: 16, width: 24, height: 24 }} />;
  }

  return (
    <>
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 400, p: 3, position: 'relative', height: '100%' }}>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size="large">
          <CloseIcon />
        </IconButton>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Documents
        </Typography>
        <Box
          {...getRootProps()}
          sx={{
            border: isDragActive ? '3px dashed #1976d2' : '2px dashed #ccc',
            borderRadius: 2,
            bgcolor: isDragActive ? 'rgba(25, 118, 210, 0.08)' : 'background.paper',
            p: 4,
            mb: 2,
            textAlign: 'center',
            cursor: uploading ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: '#1976d2',
              bgcolor: 'rgba(25, 118, 210, 0.04)'
            },
            minHeight: 120,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <Typography color="primary" variant="h6" sx={{ mb: 1 }}>
              Drop the file here…
            </Typography>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                Drag & drop a file here
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                or click to browse from your device
              </Typography>
            </>
          )}
          {!isDragActive && (
            <Button 
              variant="contained" 
              component="span" 
              disabled={uploading} 
              sx={{ mt: 1 }}
              size="small"
            >
              {uploading ? <CircularProgress size={20} color="inherit" /> : 'Choose File'}
            </Button>
          )}
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Uploaded Documents
          </Typography>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <List>
              {documents.length === 0 && (
                <ListItem>
                  <ListItemText primary="No documents uploaded yet." />
                </ListItem>
              )}
              {documents.map((doc) => (
                <ListItem
                  key={doc._id}
                  disableGutters
                  sx={{
                    borderBottom: '1px solid #eee',
                    py: 1,
                    alignItems: 'center'
                  }}
                  secondaryAction={
                    <IconButton edge="end" aria-label="delete"
                     onClick={() => handleDeleteDocument(doc._id)}
                     >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d32f2f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m5 0V4a2 2 0 0 1 2-2h0a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                    </IconButton>
                  }
                >
                  {getFileIcon(doc.file_name)}
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        component="a"
                        // href={doc.public_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary',
                          wordBreak: 'break-all'
                        }}
                      >
                        {doc.file_name}
                      </Typography>
                    }
                    secondary={
                      doc.createdAt ? (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Uploaded: {new Date(doc.createdAt).toLocaleString()}
                          </Typography>
                        </Box>
                      ) : null
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
    </Drawer>
    <Dialog open={deleteDialogOpen} onClose={cancelDeleteDocument}>
  <DialogTitle>Delete Document</DialogTitle>
  <DialogContent>
    <DialogContentText>
      Are you sure you want to delete this document? This action cannot be undone.
    </DialogContentText>
  </DialogContent>
  <DialogActions>
    <Button onClick={cancelDeleteDocument} color="primary">
      Cancel
    </Button>
    <Button onClick={confirmDeleteDocument} color="error" autoFocus>
      Delete
    </Button>
  </DialogActions>
</Dialog>
    </>
  );
};

export default DocumentsPanel;
